@file:Suppress("ktlint:standard:max-line-length")

package com.royceecoleman.bill.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.royceecoleman.bill.billing.deletePreviousBill
import com.royceecoleman.bill.billing.getPreviousBills
import com.royceecoleman.bill.billing.uploadPreviousBill
import com.royceecoleman.bill.shared.billing.PreviousBill
import com.royceecoleman.bill.shared.billing.PreviousBillEntry
import com.royceecoleman.bill.shared.billing.PreviousBillUpload
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.files.File
import org.w3c.files.FileList
import kotlin.js.Date

/** "2026-09-02 · 1.2 h · L240 · A104", leaving out what the bill did not show. */
internal fun previousBillEntryDetails(entry: PreviousBillEntry): String =
    listOf(entry.date, entry.hours.takeIf(String::isNotBlank)?.let { "$it h" }.orEmpty(), entry.taskCode, entry.activityCode)
        .filter(String::isNotBlank)
        .joinToString(" · ")

internal const val DOCX_TYPE = "application/vnd.openxmlformats-officedocument.wordprocessingml.document"

/** The file types the previous-bill and rules uploads accept. */
internal const val DOCUMENT_ACCEPT = ".pdf,.docx,application/pdf,$DOCX_TYPE"

/** A PDF or Word document picked for upload. [file] is the browser's File; tests use any value. */
internal class PickedFile<out F>(
    val name: String,
    val type: String,
    val size: Double,
    val file: F,
)

internal fun File.picked() = PickedFile(name, type, size.toDouble(), this)

private fun FileList.picked() = (0 until length).mapNotNull { index -> item(index)?.picked() }

/**
 * Why [file] cannot be uploaded as a PDF or Word (.docx) document, or null when it can; [advice] says what to upload
 * instead. The server checks again, by the file's content.
 */
internal fun documentRefusal(
    file: PickedFile<*>,
    advice: String,
): String? {
    val pdf = file.type == "application/pdf" || file.name.endsWith(".pdf", ignoreCase = true)
    val docx = file.type == DOCX_TYPE || file.name.endsWith(".docx", ignoreCase = true)
    return when {
        !pdf && !docx && (file.type == "application/msword" || file.name.endsWith(".doc", ignoreCase = true)) ->
            "${file.name} is an older Word document. Save the document as .docx or PDF, then upload it again."
        !pdf && !docx -> "${file.name} is not a PDF or Word (.docx) file. $advice"
        file.size > PreviousBill.MAX_FILE_BYTES -> "${file.name} is larger than 20 MB. Choose a file of 20 MB or less."
        else -> null
    }
}

/** Why [file] cannot be uploaded as a previous bill, or null when it can. */
internal fun previousBillRefusal(file: PickedFile<*>): String? =
    documentRefusal(file, "Export the bill from your billing software as a PDF or Word document.")

/** Why [file] cannot replace the standing rules, or null when it can. */
internal fun rulesFileRefusal(file: PickedFile<*>): String? = documentRefusal(file, "Upload your rules as a PDF or Word document.")

/**
 * Previous bills picked but not uploaded yet: while a new profile is unsaved, they wait here (in memory only) until it is
 * saved. [refused] explains the files the last [add] left out.
 */
internal data class PreviousBillQueue<out F>(
    val files: List<PickedFile<F>> = emptyList(),
    val refused: List<String> = emptyList(),
)

/** Adds the acceptable files of [picked], in order, while the queue holds fewer than [limit]. */
internal fun <F> PreviousBillQueue<F>.add(
    picked: List<PickedFile<F>>,
    limit: Int = PreviousBill.MAX_PER_PROFILE,
): PreviousBillQueue<F> {
    val files = this.files.toMutableList()
    val refused = mutableListOf<String>()
    picked.forEach { file ->
        val reason = previousBillRefusal(file) ?: "A profile holds up to ${PreviousBill.MAX_PER_PROFILE} previous bills, so ${file.name} was not added.".takeIf { files.size >= limit }
        if (reason == null) files += file else refused += reason
    }
    return PreviousBillQueue(files, refused)
}

internal fun <F> PreviousBillQueue<F>.remove(index: Int) = PreviousBillQueue(files.filterIndexed { i, _ -> i != index })

/** The bills read, and "file: reason" for each file that failed. */
internal class PreviousBillUploads(
    val saved: List<PreviousBill>,
    val failures: List<String>,
)

/** Uploads [files] one at a time, in order. A failed file does not stop the others. [progress] gets a status line before each. */
internal suspend fun <F> uploadPreviousBills(
    files: List<PickedFile<F>>,
    progress: (String) -> Unit,
    upload: suspend (PickedFile<F>) -> PreviousBill,
): PreviousBillUploads {
    val saved = mutableListOf<PreviousBill>()
    val failures = mutableListOf<String>()
    files.forEachIndexed { index, file ->
        progress("Reading previous bill ${index + 1} of ${files.size}… this can take a minute")
        try {
            saved += upload(file)
        } catch (cancel: CancellationException) {
            throw cancel
        } catch (e: Exception) {
            failures += "${file.name}: ${e.message ?: "The upload failed."}"
        }
    }
    return PreviousBillUploads(saved, failures)
}

internal suspend fun uploadPickedBill(
    profileId: String,
    picked: PickedFile<File>,
) = uploadPreviousBill(profileId, PreviousBillUpload(picked.name, readFileBase64(picked.file)))

/**
 * Optional examples of finished work: last months' final bills from the lawyer's own billing software. A saved profile
 * uploads them at once; an unsaved one keeps them in [queue] until it is saved. [changeQueue] applies its change to the
 * current queue, since files can arrive again before this recomposes.
 */
@Composable
internal fun PreviousBills(
    profileId: String,
    busy: Boolean,
    queue: PreviousBillQueue<File>,
    changeQueue: ((PreviousBillQueue<File>) -> PreviousBillQueue<File>) -> Unit,
) {
    Div({ classes("bill-card") }) {
        H2 { Text("Previous bills (optional)") }
        P({ classes("bill-muted") }) {
            Text(
                "Upload last month's final bill from your billing software (PDF or Word .docx). BILL reads its fee entries, not the file, so drafts for this profile can match your phrasing, level of detail and how you split work into entries. Facts, dates and time are never copied from them. Up to ${PreviousBill.MAX_PER_PROFILE} bills per profile; 20 MB and ${PreviousBill.MAX_PAGES} pages each. Uploads use no drafting credits.",
            )
        }
        if (profileId.isEmpty()) {
            QueuedPreviousBills(busy, queue, changeQueue)
        } else {
            PreviousBillList(profileId, busy)
        }
    }
}

/** The upload button, inside a zone that also takes dropped PDF and Word files. */
@Composable
private fun PreviousBillPicker(
    disabled: Boolean,
    pick: (List<PickedFile<File>>) -> Unit,
) {
    var over by remember { mutableStateOf(false) }
    Div({
        classes("bill-drop")
        if (over && !disabled) classes("over")
        onDragOver { event ->
            // Without this the browser opens the file itself, discarding the page.
            event.preventDefault()
            over = true
        }
        onDragLeave { over = false }
        onDrop { event ->
            event.preventDefault()
            over = false
            val files = event.dataTransfer?.files
            if (!disabled && files != null) pick(files.picked())
        }
    }) {
        Label(attrs = {
            classes("bill-button", "primary", "bill-upload")
            if (disabled) classes("disabled")
        }) {
            Input(InputType.File, attrs = {
                classes("bill-sr-only")
                attr("accept", DOCUMENT_ACCEPT)
                attr("multiple", "")
                attr("aria-label", "Upload previous bill (PDF or Word)")
                if (disabled) attr("disabled", "")
                onChange { event ->
                    val input = event.target
                    val files = input.files?.picked().orEmpty()
                    input.value = ""
                    if (files.isNotEmpty()) pick(files)
                }
            })
            Text("Upload previous bill (PDF or Word)")
        }
        Span({ classes("bill-muted") }) { Text("Drop a PDF or Word file here") }
    }
}

@Composable
private fun Problems(messages: List<String>) {
    if (messages.isNotEmpty()) {
        P({
            classes("bill-alert")
            attr("role", "alert")
        }) { Text(messages.joinToString("\n")) }
    }
}

@Composable
private fun QueuedPreviousBills(
    busy: Boolean,
    queue: PreviousBillQueue<File>,
    changeQueue: ((PreviousBillQueue<File>) -> PreviousBillQueue<File>) -> Unit,
) {
    PreviousBillPicker(busy || queue.files.size >= PreviousBill.MAX_PER_PROFILE) { picked -> changeQueue { it.add(picked) } }
    Problems(queue.refused)
    if (queue.files.isNotEmpty()) {
        P({ classes("bill-muted") }) { Text("Will be read when you save this profile") }
        Ul {
            queue.files.forEachIndexed { index, file ->
                Li({ classes("bill-row", "bill-between") }) {
                    Span { Text(file.name) }
                    Button(attrs = {
                        classes("bill-button")
                        attr("type", "button")
                        attr("aria-label", "Remove ${file.name}")
                        if (busy) attr("disabled", "")
                        onClick { changeQueue { it.remove(index) } }
                    }) { Text("Remove") }
                }
            }
        }
    }
}

@Composable
private fun PreviousBillList(
    profileId: String,
    busy: Boolean,
) {
    var bills by remember(profileId) { mutableStateOf<List<PreviousBill>?>(null) }
    var errors by remember(profileId) { mutableStateOf(emptyList<String>()) }
    var progress by remember(profileId) { mutableStateOf("") }
    val scope = rememberCoroutineScope()
    LaunchedEffect(profileId) {
        try {
            bills = getPreviousBills(profileId)
        } catch (cancel: CancellationException) {
            throw cancel
        } catch (e: Exception) {
            errors = listOf(e.message.orEmpty())
        }
    }
    val list = bills
    val full = list != null && list.size >= PreviousBill.MAX_PER_PROFILE
    val uploading = progress.isNotEmpty()
    PreviousBillPicker(busy || uploading || full) { picked ->
        val batch = PreviousBillQueue<File>().add(picked, PreviousBill.MAX_PER_PROFILE - bills.orEmpty().size)
        errors = batch.refused
        if (batch.files.isNotEmpty()) {
            scope.launch {
                try {
                    val result = uploadPreviousBills(batch.files, { progress = it }) { uploadPickedBill(profileId, it) }
                    bills = result.saved.reversed() + bills.orEmpty()
                    errors = errors + result.failures
                } finally {
                    progress = ""
                }
            }
        }
    }
    if (full) P({ classes("bill-muted") }) { Text("This profile has ${PreviousBill.MAX_PER_PROFILE} previous bills. Delete one to upload another.") }
    if (uploading) {
        P({
            classes("bill-muted")
            attr("role", "status")
        }) { Text(progress) }
    }
    Problems(errors)
    if (list != null && list.isEmpty()) P({ classes("bill-muted") }) { Text("No previous bills yet.") }
    list?.forEach { bill ->
        key(bill.id) {
            Div({ classes("bill-card") }) {
                Div({ classes("bill-row", "bill-between") }) {
                    Span {
                        Text("${bill.filename} · uploaded ${Date(bill.uploadedAt.toDouble()).toISOString().take(10)} · ${bill.entryCount} ${if (bill.entryCount == 1) "entry" else "entries"}")
                    }
                    Button(attrs = {
                        classes("bill-button", "danger")
                        attr("type", "button")
                        attr("aria-label", "Delete previous bill ${bill.filename}")
                        if (busy || uploading) attr("disabled", "")
                        onClick {
                            if (window.confirm("Delete this previous bill? Future drafts will no longer use its entries.")) {
                                scope.launch {
                                    try {
                                        deletePreviousBill(profileId, bill.id)
                                        bills = bills?.filterNot { it.id == bill.id }
                                    } catch (cancel: CancellationException) {
                                        throw cancel
                                    } catch (e: Exception) {
                                        errors = listOf(e.message.orEmpty())
                                    }
                                }
                            }
                        }
                    }) { Text("Delete") }
                }
                BillDetails("Entries in ${bill.filename}") {
                    Ol {
                        bill.entries.forEach { entry ->
                            Li {
                                Text(entry.narrative)
                                val details = previousBillEntryDetails(entry)
                                if (details.isNotEmpty()) Span({ classes("bill-muted") }) { Text(" ($details)") }
                            }
                        }
                    }
                }
            }
        }
    }
}
